import React from "react";

import { cn } from "@/lib/utils";

type Element = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";

interface HTMLTag {
  as?: Element;
}

const Heading1 = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<"h1"> & HTMLTag
>(({ className, as: Component = "h1", ...props }, ref) => (
  <Component
    ref={ref}
    className={cn(
      "text-heading-40 md:text-heading-40 xl:text-heading-48 text-black",
      className
    )}
    {...props}
  />
));
Heading1.displayName = "Heading1";

const Heading2 = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<"h2"> & HTMLTag
>(({ className, as: Component = "h2", ...props }, ref) => (
  <Component
    ref={ref}
    className={cn("text-heading-28 lg:text-heading-48 text-black", className)}
    {...props}
  />
));
Heading2.displayName = "Heading2";

const Heading3 = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<"h3"> & HTMLTag
>(({ className, as: Component = "h3", ...props }, ref) => (
  <Component
    ref={ref}
    className={cn("text-heading-24 lg:text-heading-40 text-black", className)}
    {...props}
  />
));
Heading3.displayName = "Heading3";

const Heading4 = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<"h4"> & HTMLTag
>(({ className, as: Component = "h4", ...props }, ref) => (
  <Component
    ref={ref}
    className={cn("text-heading-20 lg:text-heading-24 text-black", className)}
    {...props}
  />
));
Heading4.displayName = "Heading4";

const Heading5 = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<"h5"> & HTMLTag
>(({ className, as: Component = "h5", ...props }, ref) => (
  <Component
    ref={ref}
    className={cn("text-heading-20 lg:text-heading-20 text-black", className)}
    {...props}
  />
));
Heading5.displayName = "Heading5";

const Heading6 = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<"h6"> & HTMLTag
>(({ className, as: Component = "h6", ...props }, ref) => (
  <Component
    ref={ref}
    className={cn(
      "text-heading-16 xl:text-heading-20 text-black uppercase",
      className
    )}
    {...props}
  />
));
Heading6.displayName = "Heading6";

const TypographyMuted = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<"p">
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-body-sm text-text-inactive", className)}
    {...props}
  />
));
TypographyMuted.displayName = "TypographyMuted";

const TypographyP = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<"p">
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-body text-black", className)} {...props} />
));
TypographyP.displayName = "TypographyP";

export {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  TypographyMuted,
  TypographyP,
};
